import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import { useParams, Navigate } from "react-router-dom";
import { LocalizedLink, useCurrentLang, localizePath } from "@/i18n/language";
import { img } from "@/lib/images";
import NatureArticle from "@/pages/bali-info-detail/components/NatureArticle";
import RiceArticle from "@/pages/bali-info-detail/components/RiceArticle";
import { plantsContent } from "@/pages/bali-info-detail/data/plants";
import { fruitsContent } from "@/pages/bali-info-detail/data/fruits";
import { spicesContent } from "@/pages/bali-info-detail/data/spices";
import { riceContent } from "@/pages/bali-info-detail/data/rice";

const baliInfoSubLinks = [
  { key: "bali_info_sidebar_general", slug: "general" },
  { key: "bali_info_sidebar_about", slug: "about-bali" },
  { key: "bali_info_sidebar_books", slug: "books" },
  { key: "bali_info_sidebar_plants", slug: "plants" },
  { key: "bali_info_sidebar_fruits", slug: "fruits" },
  { key: "bali_info_sidebar_spices", slug: "spices" },
  { key: "bali_info_sidebar_rice", slug: "rice" },
];

const generalItems = [
  { label: "Airporttax", textKey: "bali_info_general_airporttax" },
  { label: "Arzt", textKey: "bali_info_general_arzt" },
  { label: "Ausflüge", textKey: "bali_info_general_ausfluege" },
  { label: "Baden im Meer", textKey: "bali_info_general_baden" },
  { label: "Cargo (Spedition)", textKey: "bali_info_general_cargo" },
  { label: "Einkaufen (Shopping)", textKey: "bali_info_general_einkaufen" },
  { label: "Essen und Trinken", textKey: "bali_info_general_essen" },
  { label: "Fotografieren und Filmen", textKey: "bali_info_general_fotografieren" },
  { label: "Geldwechsel", textKey: "bali_info_general_geldwechsel" },
  { label: "Handtücher und Liegen", textKey: "bali_info_general_handtuecher" },
  { label: "Kreditkarten", textKey: "bali_info_general_kreditkarten" },
  { label: "Mietwagen", textKey: "bali_info_general_mietwagen" },
  { label: "Post", textKey: "bali_info_general_post" },
  { label: "Reiseleitung", textKey: "bali_info_general_reiseleitung" },
  { label: "Safe für Wertsachen", textKey: "bali_info_general_safe" },
  { label: "Stromspannung", textKey: "bali_info_general_strom" },
  { label: "Taxis, Transfer und Transportmittel", textKey: "bali_info_general_taxi" },
  { label: "Telefon und Internet", labelKey: "bali_info_general_telefon_label", textKey: "bali_info_general_telefon" },
  { label: "Trinkgeld", textKey: "bali_info_general_trinkgeld" },
  { label: "Umweltschutz", textKey: "bali_info_general_umweltschutz" },
  { label: "Visum / Einreise", textKey: "bali_info_general_visum" },
  { label: "Zeitunterschied", textKey: "bali_info_general_zeit" },
  { label: "Zimmerbenutzung", textKey: "bali_info_general_zimmer" },
];

const entryInfo = [
  {
    titleKey: "bali_info_entry_arrival_title",
    textKey: "bali_info_entry_arrival_text",
    link: "https://allindonesia.imigrasi.go.id/",
    linkLabel: "allindonesia.imigrasi.go.id",
  },
  {
    titleKey: "bali_info_entry_visa_title",
    textKey: "bali_info_entry_visa_text",
    link: "https://evisa.imigrasi.go.id",
    linkLabel: "evisa.imigrasi.go.id",
  },
  {
    titleKey: "bali_info_entry_customs_title",
    textKey: "bali_info_entry_customs_text",
    link: "https://ecd.beacukai.go.id",
    linkLabel: "ecd.beacukai.go.id",
  },
  {
    titleKey: "bali_info_entry_health_title",
    textKey: "bali_info_entry_health_text",
  },
  {
    titleKey: "bali_info_entry_tax_title",
    textKey: "bali_info_entry_tax_text",
    link: "https://lovebali.baliprov.go.id",
    linkLabel: "lovebali.baliprov.go.id",
  },
];

export default function BaliInfoDetailPage() {
  const { t } = useTranslation();
  const { slug: rawSlug } = useParams<{ slug: string }>();
  const lang = useCurrentLang();

  // Alte deutsche Adressen dauerhaft auf die englischen Slugs umleiten
  const legacySlugs: Record<string, string> = {
    reis: "rice",
    pflanzen: "plants",
    fruechte: "fruits",
    gewuerze: "spices",
  };
  const legacyTarget = rawSlug ? legacySlugs[rawSlug] : undefined;

  if (legacyTarget) {
    return <Navigate to={localizePath(`/bali-info/${legacyTarget}`, lang)} replace />;
  }

  const slug = rawSlug;

  const slugToKeySuffix: Record<string, string> = {
    general: "general",
    "about-bali": "about",
    books: "books",
    plants: "plants",
    fruits: "fruits",
    spices: "spices",
    rice: "rice",
  };

  const keySuffix = slugToKeySuffix[slug || ""] || "";
  const eyebrow = keySuffix ? t(`bali_info_${keySuffix}_eyebrow`) : t("bali_info_overview_eyebrow");
  const title = keySuffix ? t(`bali_info_${keySuffix}_title`) : t("bali_info_overview_title");
  const intro = keySuffix ? t(`bali_info_${keySuffix}_intro`) : t("bali_info_overview_intro");

  const imageMap: Record<string, string> = {
    general: img("bali-info/general/header-general-01.jpg"),
    "about-bali": img("bali-info/about-bali/header-about-bali-01.jpg"),
    books: img("bali-info/books/header-books-01.jpg"),
    plants:
      "https://readdy.ai/api/search-image?query=Dense%20tropical%20vegetation%20on%20Bali%20with%20huge%20banana%20leaves%2C%20tree%20ferns%2C%20heliconia%20and%20bamboo%2C%20morning%20light%20filtering%20through%20the%20canopy%2C%20deep%20green%20tones%2C%20atmospheric%20nature%20photography&width=1600&height=900&seq=bali-info-plants-header-01&orientation=landscape",
    fruits:
      "https://readdy.ai/api/search-image?query=Colourful%20tropical%20fruits%20of%20Bali%20arranged%20on%20a%20rustic%20wooden%20table%2C%20mango%2C%20rambutan%2C%20mangosteen%2C%20pineapple%20and%20bananas%2C%20warm%20sunlight%2C%20soft%20neutral%20background%2C%20editorial%20food%20photography&width=1600&height=900&seq=bali-info-fruits-header-01&orientation=landscape",
    spices:
      "https://readdy.ai/api/search-image?query=Traditional%20Balinese%20spices%20in%20small%20wooden%20bowls%2C%20cloves%2C%20nutmeg%2C%20cinnamon%2C%20star%20anise%2C%20ginger%20and%20turmeric%2C%20dark%20rustic%20table%2C%20warm%20directional%20light%2C%20moody%20still%20life%20photography&width=1600&height=900&seq=bali-info-spices-header-01&orientation=landscape",
    rice: img("balivwtour/rice/header-rice-01.jpg"),
  };

  const renderContent = () => {
    switch (slug) {
      case "general":
        return (
          <div>
            {/* --- Einreise nach Indonesien --- */}
            <div className="rounded-lg border border-accent-200 bg-accent-50/70 p-5 md:p-6 mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 text-background-50 flex-shrink-0">
                  <i className="ri-passport-line text-lg"></i>
                </div>
                <h3 className="font-heading text-xl text-foreground-950">
                  {t("bali_info_entry_title")}
                </h3>
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                {t("bali_info_entry_intro")}
              </p>
              <div className="space-y-5">
                {entryInfo.map((item) => (
                  <div
                    key={item.titleKey}
                    className="border-l-2 border-accent-600 pl-4"
                  >
                    <h4 className="font-heading text-base text-foreground-950 mb-1">
                      {t(item.titleKey)}
                    </h4>
                    <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                      {t(item.textKey)}
                    </p>
                    {item.link ? (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-accent-700 hover:text-accent-800 mt-2 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-external-link-line"></i>
                        {item.linkLabel}
                      </a>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            {/* --- Informationen von A–Z --- */}
            <h3 className="font-heading text-xl text-foreground-950 mb-4">
              {t("bali_info_general_az_title")}
            </h3>
            <div className="space-y-4">
              {generalItems.map((item) => (
                <div
                  key={item.label}
                  className="border-l-2 border-accent-500 pl-4 py-2"
                >
                  <h3 className="font-heading text-lg text-foreground-950 mb-1">
                    {item.labelKey ? t(item.labelKey) : item.label}
                  </h3>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {t(item.textKey)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        );
      case "about-bali":
        return (
          <div className="space-y-6">
            {/* --- Bali, eine kurze Beschreibung --- */}
            <h3 className="font-heading text-xl text-foreground-950">
              {t("bali_info_about_title")}
            </h3>

            <p className="text-sm text-foreground-700 leading-relaxed">
              {t("bali_info_about_p1")}
            </p>

            <div className="flex flex-col md:flex-row gap-4 items-start">
              <p className="text-sm text-foreground-700 leading-relaxed flex-1">
                {t("bali_info_about_p2")}
              </p>
              <img
                src={img("bali-info/about-bali/about-bali-01.jpg")}
                alt="Bali Reisfeldlandschaft mit Batukaru Berg im Hintergrund"
                className="w-full md:w-72 md:h-48 rounded-lg object-cover flex-shrink-0"
              />
            </div>

            <p className="text-sm text-foreground-700 leading-relaxed">
              {t("bali_info_about_p3")}
            </p>

            <div className="flex flex-col md:flex-row gap-4 items-start">
              <img
                src={img("bali-info/about-bali/about-bali-02.jpg")}
                alt="Bali Tempelzeremonie im Dorf"
                className="w-full md:w-72 md:h-48 rounded-lg object-cover flex-shrink-0"
              />
              <p className="text-sm text-foreground-700 leading-relaxed flex-1">
                {t("bali_info_about_p4")}
              </p>
            </div>

            <p className="text-sm text-foreground-700 leading-relaxed">
              {t("bali_info_about_p5")}
            </p>

            <p className="text-sm text-foreground-700 leading-relaxed">
              {t("bali_info_about_p6")}
            </p>

            <div className="flex flex-col md:flex-row gap-4 items-start">
              <p className="text-sm text-foreground-700 leading-relaxed flex-1">
                {t("bali_info_about_p7")}
              </p>
              <img
                src={img("bali-info/about-bali/about-bali-03.jpg")}
                alt="Strassenrestaurant auf Bali"
                className="w-full md:w-72 md:h-48 rounded-lg object-cover flex-shrink-0"
              />
            </div>

            <p className="text-sm text-foreground-700 leading-relaxed">
              {t("bali_info_about_p8")}
            </p>

            <p className="text-sm text-foreground-700 leading-relaxed">
              {t("bali_info_about_p9")}
            </p>

            {/* --- Geschichte Balis --- */}
            <h3 className="font-heading text-xl text-foreground-950 mt-8">
              {t("bali_info_history_title")}
            </h3>

            <div className="flex flex-col md:flex-row gap-4 items-start">
              <p className="text-sm text-foreground-700 leading-relaxed flex-1">
                {t("bali_info_history_p1")}
              </p>
              <img
                src={img("bali-info/about-bali/about-bali-04.jpg")}
                alt="Bali Hindupriester um 1890"
                className="w-full md:w-72 md:h-48 rounded-lg object-cover flex-shrink-0"
              />
            </div>

            <p className="text-sm text-foreground-700 leading-relaxed">
              {t("bali_info_history_p2")}
            </p>

            <div className="flex flex-col md:flex-row gap-4 items-start">
              <img
                src={img("bali-info/about-bali/about-bali-05.jpg")}
                alt="Bali Lontarblattbuch"
                className="w-full md:w-72 md:h-48 rounded-lg object-cover flex-shrink-0"
              />
              <p className="text-sm text-foreground-700 leading-relaxed flex-1">
                {t("bali_info_history_p3")}
              </p>
            </div>

            <p className="text-sm text-foreground-700 leading-relaxed">
              {t("bali_info_history_p4")}
            </p>

            {/* --- Verweis auf die eigene Reis-Seite --- */}
            <div className="mt-8 rounded-lg border border-accent-200 bg-accent-50/70 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 text-background-50 flex-shrink-0">
                <i className="ri-plant-line text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-lg text-foreground-950">
                  {t("bali_info_sidebar_rice")}
                </h3>
                <p className="text-sm text-foreground-700 leading-relaxed mt-1">
                  {t("bali_info_rice_teaser")}
                </p>
              </div>
              <LocalizedLink
                to="/bali-info/rice"
                className="inline-flex items-center gap-2 text-sm font-medium text-accent-700 hover:text-accent-800 whitespace-nowrap cursor-pointer"
              >
                {t("bali_info_rice_teaser_cta")}
                <i className="ri-arrow-right-line"></i>
              </LocalizedLink>
            </div>
          </div>
        );
      case "books":
        return (
          <div className="space-y-4">
            <p className="text-sm text-foreground-700 leading-relaxed font-medium">
              {t("bali_info_books_intro")}
            </p>
            {t("bali_info_books_text")
              .split("\n\n")
              .map((line, i) => (
                <p key={i} className="text-sm text-foreground-700 leading-relaxed">
                  {line}
                </p>
              ))}
          </div>
        );
      case "plants":
        return <NatureArticle content={plantsContent} />;
      case "fruits":
        return <NatureArticle content={fruitsContent} />;
      case "spices":
        return <NatureArticle content={spicesContent} />;
      case "rice":
        return <RiceArticle content={riceContent} />;
      default:
        return (
          <p className="text-sm text-foreground-700">
            {t("bali_info_back_to_overview")}
          </p>
        );
    }
  };

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={eyebrow}
          title={title}
          subtitle={intro}
          image={imageMap[slug || "general"] || imageMap["general"]}
          imageAlt={title}
        />

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-background-100 border border-background-200 rounded-lg p-6 sticky top-24">
                  <div className="eyebrow-brand text-xs uppercase tracking-[0.3em] mb-3">
                    — {t("bali_info_sidebar_title")} —
                  </div>
                  <nav className="space-y-1">
                    {baliInfoSubLinks.map((sub) => (
                      <LocalizedLink
                        key={sub.slug}
                        to={`/bali-info/${sub.slug}`}
                        className={`block px-3 py-2.5 rounded-md text-sm transition-colors ${
                          slug === sub.slug
                            ? "bg-accent-50 text-accent-700 font-medium"
                            : "text-foreground-700 hover:bg-accent-100 hover:text-accent-700"
                        }`}
                      >
                        {t(sub.key)}
                      </LocalizedLink>
                    ))}
                  </nav>
                  <div className="mt-6 pt-4 border-t border-background-200">
                    <LocalizedLink
                      to="/bali-info"
                      className="inline-flex items-center gap-2 text-sm text-accent-700 hover:text-accent-800 transition-colors"
                    >
                      <i className="ri-arrow-left-line"></i>
                      {t("bali_info_back_to_overview")}
                    </LocalizedLink>
                  </div>
                </div>
              </div>

              {/* Main content */}
              <div className="lg:col-span-2">
                <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
                  {renderContent()}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}